import { useState, useCallback, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";

import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  makeStyles,
  Box,
  Checkbox,
  Button,
  CircularProgress,
} from "@material-ui/core";

import Api from "lib/api/httpRequestHelper";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import useStructureQueries from "hooks/useStructureQueries";

import HeaderClientes from "components/_refactor/headers/clients";

import Filters from "./components/filters";
import RepresentativeCard from "./components/representative-card";

import componentStyles from "assets/theme/views";

const useStyles = makeStyles(componentStyles);

export default function PublicarVitrine() {
  const [isLoading, setIsLoading] = useState(false);
  const [customersList, setCustomersList] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [filters, setFilters] = useState({
    status: { value: "TODOS", label: "Todos" },
    region: null,
    search: "",
    grupoEconomico: null,
    razaoSocial: null,
    cnpj: "",
  });
  const [grupoEconomicoOptions, setGrupoEconomicoOptions] = useState([]);
  const [razaoSocialOptions, setRazaoSocialOptions] = useState([]);
  const history = useHistory();
  const searchParams = new URLSearchParams(window.location.search);
  const classes = useStyles();
  const id = searchParams.get("id");

  const snackbar = useSnackbar();
  const { getEconomicGroups, getRepresentatives } = useStructureQueries();

  const filteredCustomers = useMemo(() => {
    return customersList.filter((representative) => {
      const matchSearch =
        !filters?.search?.trim() ||
        (representative.Nome || "")
          .toLowerCase()
          .includes(filters.search.toLowerCase());
      const grupoValue =
        filters.grupoEconomico?.Id ?? filters.grupoEconomico?.Nome ?? "";
      const matchGrupoEconomico =
        !grupoValue ||
        representative.IdGrupoEconomico === filters.grupoEconomico?.Id ||
        (representative.GrupoEconomico || "").toLowerCase() ===
          (filters.grupoEconomico?.Nome || "").toLowerCase();
      const razaoValue =
        filters.razaoSocial?.Id ?? filters.razaoSocial?.Nome ?? "";
      const matchRazaoSocial =
        !razaoValue ||
        representative.IdPessoaLoja === filters.razaoSocial?.Id ||
        (representative.RazaoSocial || representative.Nome || "")
          .toLowerCase()
          .includes((filters.razaoSocial?.Nome || "").toLowerCase());
      const cnpjDigits = (value) => (value || "").replace(/\D/g, "");
      const matchCnpj =
        !filters?.cnpj?.trim() ||
        cnpjDigits(representative.CnpjCpf || representative.Cnpj).includes(
          cnpjDigits(filters.cnpj),
        );
      return (
        matchSearch && matchGrupoEconomico && matchRazaoSocial && matchCnpj
      );
    });
  }, [customersList, filters]);

  const handleCheckboxChange = (id, checkAll) => {
    if (checkAll) {
      if (selectedCustomers.length === customersList.length) {
        setSelectedCustomers([]);
      } else {
        setSelectedCustomers(
          customersList.map((representative) => representative.IdPessoaLoja),
        );
      }

      return;
    }

    if (selectedCustomers.includes(id)) {
      setSelectedCustomers(
        selectedCustomers.filter((representativeId) => representativeId !== id),
      );
    } else {
      setSelectedCustomers([...selectedCustomers, id]);
    }
  };

  const handleGetCustomers = useCallback(async () => {
    try {
      const params = new URLSearchParams();

      if (filters.region?.value) {
        params.append("estado", filters.region?.value);
      }
      if (filters.status?.value && filters.status?.value !== "TODOS") {
        params.append("status", filters.status?.value);
      }
      const grupoValue =
        filters.grupoEconomico?.Id ?? filters.grupoEconomico?.Nome;
      if (grupoValue) {
        params.append("idGrupoEconomico", String(grupoValue));
      }
      const razaoValue = filters.razaoSocial?.Id ?? filters.razaoSocial?.Nome;
      if (razaoValue) {
        params.append("idPessoaLoja", String(razaoValue));
      }
      if (filters.cnpj?.trim()) {
        params.append(
          "cnpjRepresentante",
          (filters.cnpj || "").replace(/\D/g, ""),
        );
      }

      setIsLoading(true);

      const pageSize = 100;
      let page = 0;
      const aggregated = [];
      const truePage = true;
      while (truePage) {
        const pagedParams = new URLSearchParams(params);
        pagedParams.set("page", String(page));
        pagedParams.set("pageSize", String(pageSize));

        const response = await Api.get(
          "representante-clientes",
          "representative",
          `?${pagedParams.toString()}`,
          true,
        );

        const batch = response.Result?.Result ?? [];
        aggregated.push(...batch);
        if (batch.length < pageSize) {
          break;
        }
        page += 1;
        if (page > 500) {
          break;
        }
      }

      const uniqueCustomers = aggregated.filter(
        (rep, idx, arr) =>
          arr.findIndex((item) => item.IdPessoaLoja === rep.IdPessoaLoja) ===
          idx,
      );
      setCustomersList(uniqueCustomers);
    } catch (error) {
      console.log(error);
      snackbar.showMessage("Erro ao buscar clientes. Tente novamente.", {
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    filters.region,
    filters.status,
    filters.grupoEconomico,
    filters.razaoSocial,
    filters.cnpj,
  ]);

  const handlePublish = async () => {
    try {
      const response = await Api.post(
        "create-virtual-b2b-cliente",
        "showcaseB2B",
        {
          idVitrineVirtualB2b: Number(id),
          idClientes: selectedCustomers.toString(),
        },
        true,
      );

      if (response.Success) {
        history.push("/admin/visualizar-vitrines-b2b");
        snackbar.showMessage(response.Result.MessageResponse, {
          severity: "success",
        });
      }
    } catch (error) {
      console.error(error);
      snackbar.showMessage(error, { severity: "error" });
    }
  };

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const [economicGroupsResult, representativesResult] = await Promise.all(
          [getEconomicGroups(), getRepresentatives({})],
        );
        const economicGroups = economicGroupsResult?.data ?? [];
        const groups = Array.isArray(economicGroups)
          ? economicGroups.map((item) => ({
              Id: item.Id ?? item.IdGrupoEconomico,
              Nome: item.Nome,
            }))
          : [];
        const reps = representativesResult?.data ?? [];
        const razaoOptions = Array.isArray(reps)
          ? reps.map((item) => ({
              Id: item.Id ?? item.IdPessoa ?? item.IdPessoaLoja,
              Nome: item.Nome ?? item.RazaoSocial,
            }))
          : [];
        setGrupoEconomicoOptions(groups);
        setRazaoSocialOptions(razaoOptions);
      } catch (error) {
        setGrupoEconomicoOptions([]);
        setRazaoSocialOptions([]);
      }
    };
    loadFilterOptions();
  }, [getEconomicGroups, getRepresentatives]);

  useEffect(() => {
    handleGetCustomers();
  }, [handleGetCustomers]);

  return (
    <>
      <HeaderClientes
        section="Publicação da Vitrine – Público Alvo"
        subsection="Vitrine"
      />
      <Container maxWidth={false} style={{ marginTop: "-4.5rem" }}>
        <Card classes={{ root: classes.cardRoot }}>
          <CardContent>
            <Grid
              container
              alignItems="center"
              direction="row"
              justify="space-between"
            >
              <Typography variant="h2">
                Publicação da Vitrine – Público Alvo
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Filters
              isLoading={isLoading}
              filters={filters}
              setFilters={setFilters}
              grupoEconomicoOptions={grupoEconomicoOptions}
              razaoSocialOptions={razaoSocialOptions}
            />
            <Box
              display="flex"
              flexDirection="column"
              gap={2}
              style={{ marginTop: "2rem" }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Checkbox
                  checked={
                    customersList.every((representative) =>
                      selectedCustomers.includes(representative.IdPessoaLoja),
                    ) && customersList.length > 0
                  }
                  disabled={isLoading}
                  onChange={() => handleCheckboxChange(null, true)}
                />
                <Typography
                  variant="h6"
                  color="primary"
                  style={{
                    fontSize: "1.25rem",
                    textTransform: "uppercase",
                    margin: 0,
                  }}
                >
                  Todos
                </Typography>
              </Box>
              <Box display="flex" width="100%">
                {isLoading ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width="500px"
                    height="500px"
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <Box
                      width="500px"
                      height="500px"
                      style={{ overflowY: "scroll" }}
                    >
                      {filteredCustomers.map((representative, index) => (
                        <RepresentativeCard
                          key={index}
                          representative={representative}
                          checked={selectedCustomers.includes(
                            representative.IdPessoaLoja,
                          )}
                          onChange={() =>
                            handleCheckboxChange(representative.IdPessoaLoja)
                          }
                        />
                      ))}
                    </Box>
                  </>
                )}
              </Box>
            </Box>
            <Box
              display="flex"
              width="100%"
              style={{ marginTop: "2rem", gap: "2rem" }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handlePublish}
                disabled={isLoading || selectedCustomers.length === 0}
                loading={isLoading}
              >
                Publicar
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => history.push("/admin/visualizar-vitrines-b2b")}
                disabled={isLoading}
              >
                Voltar
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </>
  );
}
